import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useFetchEQLFields } from '@modules/common/hooks';
import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { getCanConfigureIntegrationSourceLink } from '@modules/integrationsV2/utils';
import { OpenInNewRounded } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  EqlField,
  EqlFieldValueType,
  IntegrationViewFragment,
  ObjectType,
} from 'generated/graphql';
import { SyntheticEvent, useEffect, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const NONE_EQL_FIELD_VALUE: EqlField = {
  field: '',
  displayName: 'None',
  valueType: EqlFieldValueType.String,
  isMultiValue: false,
  objectType: ObjectType.Feedback,
};

interface ConfigureSourceLinkSectionProps {
  integrationSource: IntegrationSource;
  integrations: IntegrationViewFragment[];
}

export const ConfigureSourceLinkSection = ({
  integrationSource,
  integrations,
}: ConfigureSourceLinkSectionProps) => {
  const [selectedMetadataKey, setSelectedMetadataKey] = useState('');

  const { fields: eqlFields } = useFetchEQLFields({
    fetchPolicy: 'cache-first', // Because we have "cache-and-network" on the root component, we can use "cache-first" here.
  });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: integrationSource.channel.type,
      integrationType: integrationSource.integrationType,
      successNotificationContentNode: (
        <Typography variant="body2">
          {selectedMetadataKey === ''
            ? 'Link to feedback source removed.'
            : 'Link to feedback source updated successfully!'}
        </Typography>
      ),
    });

  useEffect(() => {
    setSelectedMetadataKey(integrationSource.externalLinkKey ?? '');
  }, [integrationSource.externalLinkKey]);

  const canConfigureSourceLink =
    getCanConfigureIntegrationSourceLink(integrationSource);

  const integrationIDForSource = integrations.find(
    (integration) =>
      integration.source === integrationSource.source && integration.isActive,
  )?.id;

  if (!canConfigureSourceLink || !integrationIDForSource) {
    return null;
  }

  const eqlFieldsForSource = eqlFields?.filter(
    (field) => field.detail?.metadata?.source === integrationSource.source,
  );

  const onChange = (
    _: SyntheticEvent<Element, Event>,
    newValue: EqlField | null,
  ) => {
    if (!newValue) {
      return;
    }

    setSelectedMetadataKey(newValue.field);

    updateIntegration({
      integrationID: integrationIDForSource,
      isActive: true,
      externalLinkKey: newValue.field,
    });
  };

  const selectedValue = eqlFieldsForSource?.find(
    (field) => field.field === selectedMetadataKey,
  );

  const options = [NONE_EQL_FIELD_VALUE, ...eqlFieldsForSource];

  const isUpdateInProgress = IsLoading(updateIntegrationStatus);

  return (
    <Stack
      component={Paper}
      direction="row"
      justifyContent="space-between"
      variant="outlined"
      spacing={2}
      sx={{
        px: 2,
        py: 2.5,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <OpenInNewRounded
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography>Configure link to external feedback source</Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          pl={4}
          maxWidth="600px"
        >
          You can set a metadata field as the link to the external feedback
          source, ensuring easy access to the platform where the feedback is
          being pulled from, right from the feedback card.
        </Typography>
      </Stack>
      <Box width="350px" minWidth="150px">
        <Autocomplete
          value={selectedValue ?? NONE_EQL_FIELD_VALUE}
          fullWidth
          disableClearable
          options={options}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Metadata Field"
              helperText={
                isUpdateInProgress ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box>Updating...</Box>
                    <CircularLoader size={12} />
                  </Stack>
                ) : null
              }
            />
          )}
          onChange={onChange}
          isOptionEqualToValue={(option, value) =>
            option.field === value?.field
          }
          getOptionLabel={(option) =>
            option.field ? option.displayName : 'None'
          }
          disabled={isUpdateInProgress}
        />
      </Box>
    </Stack>
  );
};
